import { useEffect, useMemo, useState } from "react";

import { useLocation, useNavigate } from "react-router-dom";

import { BanknotesIcon } from "@heroicons/react/24/outline";

import DashboardStat from "../../components/dashboard/DashboardStat";
import DashboardTileMultiple from "../../components/dashboard/DashboardTileMultiple";
import EngineExportGraph from "../../components/dashboard/EngineExportGraph";
import Generic30DayGraph from "../../components/dashboard/Generic30DayGraph";
import LeadsBookedTable from "../../components/dashboard/LeadsBookedTable";
import PartsUserActivityTable from "../../components/dashboard/PartsUserActivityTable";
import StatTable from "../../components/dashboard/StatTable";
import VehiclesCollectedTable from "../../components/dashboard/VehiclesCollectedTable";
import WeeklyForecastPie from "../../components/dashboard/WeeklyForecasePie";
import Card from "../../components/shared/Card/Card";
import LoadingWheel from "../../components/shared/LoadingWheel";
import StripeProductModal from "../../components/shared/Stripe/StripeProductModal";
import { useAuth } from "../../contexts/AuthContext";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useCustomerDetails from "../../data/useCustomerDetails";
import useDashboardStats from "../../data/useDashboardStats";
import useUtilities from "../../data/useUtilities";
import { IDashboardStats } from "../../models/DashboardStats";
import { CheckoutSessionType } from "../../models/Stripe";
import { ITellerooAccount } from "../../models/Utilities";
import PriceFormat from "../../utils/priceFormat";
import PartsRemovedActivityTable from "../../components/dashboard/PartsRemovedActivityTable";
import SaleSourcePie from "../../components/dashboard/SaleSourcePie";

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { claims, loggedIn, loading } = useAuth();
  const { setInfo } = usePageNameContext();
  const { getTellerooAccount } = useUtilities();
  const { features, brand } = useCustomerDetails();
  const { stats } = useDashboardStats();
  const [isLoading, setIsLoading] = useState(true);
  const [lookupTokens, setLookupTokens] = useState(0);
  const [dashboardStats, setDashboardStats] = useState<IDashboardStats>();
  const [tellerooAccount, setTellerooAccount] = useState<ITellerooAccount>();
  const [stripeModalOpen, setStripeModalOpen] = useState(false);

  const hasParts = useMemo(() => {
    return features.data?.data?.find((f) => f.name === "Parts Tracker");
  }, [features.data]);

  const hasLeads = useMemo(() => {
    return features.data?.data?.find((f) => f.name === "Lead Management");
  }, [features.data]);

  const checkClaims = () => {
    //redirect drivers away
    if (claims?.isDriver && !claims.isStaff && !claims.isAdmin) {
      navigate("/find-lead", { state: { from: location }, replace: true });
    }

    if (claims?.isExporter && !claims.isStaff && !claims.isAdmin) {
      navigate("/exports", { state: { from: location }, replace: true });
    }

    if (!claims?.isAdmin) {
      if (hasLeads)
        navigate("/leads", { state: { from: location }, replace: true });

      if (hasParts)
        navigate("/vehicles", { state: { from: location }, replace: true });

      if (!hasLeads && !hasParts)
        navigate("/accounts", { state: { from: location }, replace: true });
    }
  };

  useEffect(() => {
    checkClaims();
  }, [claims, loading]);

  useEffect(() => {
    //Change to individual loading wheels
    if (!stats.isLoading && stats.data && !features.isLoading && loggedIn && !loading) {
      setDashboardStats(stats.data.data);

      if (getTellerooAccount?.data)
        setTellerooAccount(getTellerooAccount.data.data);

      setIsLoading(false);
    }
  }, [stats.data, getTellerooAccount?.data, loggedIn, loading, hasParts]);

  useEffect(() => {
    setInfo({
      name: "Dashboard",
      desc: "A quick glance at the current status of your business",
    });
  }, []);

  useEffect(() => {
    if (brand.isLoading) return;
    if (!brand.data) return;

    setLookupTokens(brand.data.data.lookupTokens);
  }, [brand.data, brand.isLoading]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <LoadingWheel />
      </div>
    );
  }

  const statClass = (span: number) => {

    return `col-span-1 lg:col-span-${span}`;
  };

  const spanClass = hasParts ? `lg:col-span-2` : ``;

  return (
    <>
      {/* null mutation means no validation... in this case we don't need it as I validate using the top bar modal */}
      <StripeProductModal open={stripeModalOpen} setOpen={setStripeModalOpen} mutation={null} sessionType={CheckoutSessionType.LookupToken} />

      <div className={`grid grid-cols-1 gap-3 ${hasParts ? "2xl:grid-cols-2" : ""} mt-4`}>
        {/* -------------------- VEHICLE -------------------- */}
        <Card bodyClassName="p-4 space-y-3">
          <h2 className="text-center font-semibold">Vehicle Management</h2>

          {/* ----- STATS ----- */}
          <div className={`grid grid-cols-1 md:grid-cols-2 ${hasParts ? "lg:grid-cols-6" : "lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6"} gap-3`}>
            <DashboardStat title={`${dashboardStats?.collectionsToday}`} desc="Collections Today" color="orange" className={spanClass} />
            <DashboardStat title={`${dashboardStats?.dropOffsToday}`} desc="Drop Offs" small="today" color="red" className={spanClass} />
            <DashboardStat title={`${dashboardStats?.totalVehiclesToday}`} desc="Total Vehicles" small="today" color="teal" className={spanClass} />

            <DashboardTileMultiple
              colour="red"
              values={[
                `£${dashboardStats?.roadsideTotalToday ? dashboardStats?.roadsideTotalToday.toFixed(2) : "0"}`,
                `${dashboardStats?.roadsideCountToday ? dashboardStats?.roadsideCountToday : "0"}`,
              ]}
              labels={["Roadside Payments Today", "Roadside Payments Today"]}
              subLabels={["Total", "Count"]}
              borderless={true}
              className={spanClass}
            />
            <DashboardStat title={`${lookupTokens}`} desc="Part Lookup" small="click to top up" color="amber" className={spanClass} onClick={() => setStripeModalOpen(true)} Icon={BanknotesIcon} />
            {/* <DashboardStat title={`${dashboardStats?.documentsUploadedToday ? dashboardStats?.documentsUploadedToday : "0"}`} desc="Documents Uploaded" small="today" color="lime" className={spanClass} /> */}
            <DashboardStat title={`${dashboardStats?.documentsUploadedByUniqueCustomersToday ? dashboardStats?.documentsUploadedByUniqueCustomersToday : "0"}`} desc="Documents Uploaded" small="today" color="lime" className={spanClass} />

            <DashboardStat title={`${dashboardStats?.unscheduledLeads}`} desc="Unscheduled Leads" color="amber" className={spanClass} />
            <DashboardStat title={`${dashboardStats?.unreadMessages}`} desc="Unread Messages" color="blue" onClick={() => navigate("leads?unread=1")} className={spanClass} />
            <DashboardStat title={`${dashboardStats?.rescheduledLeads}`} desc="Leads to Reschedule" color="fuchsia" onClick={() => navigate("leads?reschedule=1")} className={spanClass} />

            {hasParts && (<div className="col-span-1 hidden lg:block"></div>)}
            <DashboardStat title={`${dashboardStats?.collectionsToday}`} desc="Collections Today" color="orange" className={spanClass} />
            <DashboardTileMultiple
              colour="cyan"
              values={[
                dashboardStats?.percentageStartsDrivesLast30Days ? dashboardStats?.percentageStartsDrivesLast30Days.toFixed(2) + "%" : "",
                dashboardStats?.percentageStartsDrivesDriverLast30Days ? dashboardStats?.percentageStartsDrivesDriverLast30Days.toFixed(2) + "%" : "",
              ]}
              labels={["Last 30 Days Starts & Drives", "Last 30 Days Starts & Drives"]}
              subLabels={["Office", "Driver"]}
              borderless={true}
              className={spanClass}
            />
            {hasParts && (<div className="col-span-1 hidden lg:block"></div>)}

            <DashboardStat title={`${PriceFormat(tellerooAccount?.balance ? tellerooAccount.balance / 100 : 0)}`} desc="Telleroo Balance" color="green" className={spanClass} />
            <DashboardStat title={`${PriceFormat(tellerooAccount?.awaitingApprovalBalance ? tellerooAccount.awaitingApprovalBalance / 100 : 0)}`} desc="Awaiting Approval" color="red" className={spanClass} />
            <DashboardStat title={`${PriceFormat(dashboardStats?.totalPurchasesMonth ? dashboardStats?.totalPurchasesMonth : 0)}`} desc="Total Purchases" small="this month" color="indigo" className={spanClass} />

            <DashboardTileMultiple
              colour="lime"
              values={[
                dashboardStats?.sccConversionRate ? dashboardStats?.sccConversionRate?.toString() + "%" : "",
                dashboardStats?.ctbConversionRate ? dashboardStats?.ctbConversionRate?.toString() + "%" : "",
                dashboardStats?.mwConversionRate ? dashboardStats?.mwConversionRate?.toString() + "%" : "",
                dashboardStats?.sellAVehicleConversionRate ? dashboardStats?.sellAVehicleConversionRate?.toString() + "%" : "",
              ]}
              labels={[
                "SCC Conversion Rate",
                "CTB Conversion Rate",
                "Motorwise Conversion Rate",
                "SellAVehicle Conversion Rate",
              ]}
              subLabels={["", "", ""]}
              borderless={true}
              className={spanClass}
            />
            <DashboardTileMultiple
              colour="amber"
              values={[
                dashboardStats?.engineExportRate ? dashboardStats?.engineExportRate?.toString() + "%" : "",
                dashboardStats?.engineExportRateWeek ? dashboardStats?.engineExportRateWeek?.toString() + "%" : "",
                dashboardStats?.engineExportRateMonth ? dashboardStats?.engineExportRateMonth?.toString() + "%" : "",
              ]}
              labels={[
                "Engine Export Rate",
                "Engine Export Rate",
                "Engine Export Rate",
              ]}
              subLabels={["", "Last 7 Days", "This Month"]}
              borderless={true}
              className={spanClass}
            />
            <DashboardStat title={`${PriceFormat(dashboardStats?.averageCommissionMonth ? dashboardStats?.averageCommissionMonth : 0)}`} desc="Average Commission" small="this month" color="teal" className={spanClass} />

            <DashboardStat title={`${dashboardStats?.avgTimeToSchedule}`} desc="Average Time To Schedule" color="pink" className={hasParts ? `lg:col-span-6` : `xl:col-span-2`} />

            <DashboardStat title={`${PriceFormat(dashboardStats?.totalOutgoingDueTomorrow)}`} desc="Total Outgoing Due Tomorrow" color="indigo" className={spanClass} />
            <DashboardStat title={`${dashboardStats?.bookedToday}`} desc="Booked Today" color="emerald" className={spanClass} />
            <DashboardStat title={`${dashboardStats?.lastSCCImportDate}`} desc="Last SCC Import Date" color="amber" className={spanClass} />
          </div>

          {/* ----- TABLES ----- */}
          <div className="space-y-10 pt-10">
            <LeadsBookedTable dashboardStats={dashboardStats!} />
            <VehiclesCollectedTable dashboardStats={dashboardStats!} />
            <div className="p-3">
              <div className="mb-3 prose">
                <h3>Activity for the last 30 days</h3>
              </div>
              {/* <ActivityGraph dashboardStats={dashboardStats!} /> */}

              <div className="mb-10">
                <Generic30DayGraph
                  datasets={[
                    {
                      label: "Vehicles Collected",
                      borderColor: "#2563eb",
                      backgroundColor: "#2563eb",
                      data: dashboardStats?.vehiclesCollected,
                    },
                    {
                      label: "Booked Today",
                      borderColor: "#9333ea",
                      backgroundColor: "#9333ea",
                      data: dashboardStats?.leadsBooked,
                    },
                  ]}
                />
              </div>

            </div>
          </div>
        </Card>

        {hasParts && (
          <>
            {/* -------------------- PARTS -------------------- */}
            <Card bodyClassName="p-4 space-y-3">
              <h2 className="text-center font-semibold">Parts Management</h2>

              {/* ----- STATS ----- */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-3 gap-3">
                <DashboardStat title={`${dashboardStats?.partsSoldToday}`} desc="Parts Sold" small="today" color="orange" />
                <DashboardStat title={`${PriceFormat(dashboardStats?.partsSoldTodayPrice)}`} desc="Parts Sold (£)" small="today" color="green" />
                <DashboardStat title={`${dashboardStats?.partsRemovedToday}`} desc="Parts Removed" small="today" color="pink" />

                <DashboardStat title={`${dashboardStats?.exportEnginesRemovedToday}`} desc="Export Engines Removed" small="today" color="amber" />
                <DashboardStat title={`${dashboardStats?.listedParts}`} desc="Parts Listed" color="teal" />
                <DashboardStat title={`${dashboardStats?.partsAwaitingPicking}`} desc="Parts Awaiting Picking" color="amber" />

                <DashboardStat title={`${dashboardStats?.partsDispatchedToday}`} desc="Parts Dispatched" small="today" color="fuchsia" />
                <DashboardStat title={`${dashboardStats?.partsImagedToday}`} desc="Parts Imaged" small="today" color="orange" />
                <DashboardStat title={`${dashboardStats?.partsLocatedToday}`} desc="Parts Located" small="today" color="pink" />

              </div>

              {/* ----- TABLES ----- */}
              <div className="space-y-10 pt-10">
                <div className="p-3">
                  <WeeklyForecastPie dashboardStats={dashboardStats!} />
                </div>

                <div className="p-3">
                  <SaleSourcePie dashboardStats={dashboardStats!} />
                </div>

                <StatTable
                  day={dashboardStats?.partsWithImagesDay}
                  week={dashboardStats?.partsWithImagesWeek}
                  month={dashboardStats?.partsWithImagesMonth}
                  title="Parts Added with Images"
                  keyColumn="User"
                  valueColumn="Added"
                />

                <PartsUserActivityTable
                  dashboardStats={dashboardStats!}
                  dayKey="partsActivityDay"
                  weekKey="partsActivityWeek"
                  monthKey="partsActivityMonth"
                  title="Parts Activity"
                  keyCol="User"
                />

                <PartsRemovedActivityTable
                  dashboardStats={dashboardStats!}

                  dayKey="removedDay"
                  weekKey="removedWeek"
                  monthKey="removedMonth"
                  title="Parts Removed"
                  keyCol="User"
                />

                <div className="p-3">
                  <div className="mb-3 prose">
                    <h3>Engine Export Opportunities</h3>
                  </div>
                  <EngineExportGraph dashboardStats={dashboardStats!} />
                </div>
              </div>
            </Card>
          </>
        )}
      </div>


      {/* <div className="mt-3 grid gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-5">
        <PostcodePricesTable dashboardStats={dashboardStats!} />
      </div> */}
    </>
  );
}
